import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Calculator,
  ChefHat,
  BookOpen,
  List,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  DollarSign,
  Brain,
  Target,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es la formula basica para calcular el precio de un plato?',
    a: 'La formula mas utilizada es: PVP = Coste de materia prima / Food cost objetivo. Si tu plato cuesta 4,50 € en ingredientes y quieres un food cost del 30%, el PVP minimo seria 4,50 / 0,30 = 15 €. A partir de ahi, ajustas segun posicionamiento, competencia y valor percibido por el cliente.',
  },
  {
    q: '¿Que multiplicador debo usar para fijar el precio?',
    a: 'El multiplicador estandar en hosteleria es x3 a x3,5 sobre el coste de materia prima. Un restaurante casual puede usar x3, mientras que un fine dining puede aplicar x4 o mas. Bares de copas suelen usar x5 a x7 en bebidas. El multiplicador equivale a dividir por el food cost objetivo: x3,33 = food cost 30%.',
  },
  {
    q: '¿Que food cost es normal para un restaurante?',
    a: 'El food cost optimo depende del tipo de establecimiento: fast food 25-30%, casual dining 28-35%, fine dining 30-38%, cafeteria 20-28%, bar de copas 15-22%. El estandar general en Espana es mantenerlo entre el 28% y el 32% para la mayoria de restaurantes.',
  },
  {
    q: '¿Solo debo considerar el coste de ingredientes para fijar el precio?',
    a: 'No. El coste de materia prima es el punto de partida, pero un precio de venta rentable debe cubrir tambien costes de personal (25-35%), alquiler y gastos fijos (10-20%), energia y suministros (5-8%), amortizaciones y un margen de beneficio (5-15%). El metodo de los 4 costes te ayuda a calcular el precio real.',
  },
  {
    q: '¿Como fijo el precio de un menu del dia?',
    a: 'Escandalla cada componente del menu (primer plato, segundo, postre, bebida, pan) y suma los costes. Aplica un food cost objetivo del 30-35% (los menus del dia suelen tener margenes mas ajustados). Si el coste total es 4,20 €, el PVP minimo para un 30% seria 14 €. El menu del dia compensa margenes bajos con volumen alto.',
  },
  {
    q: '¿Debo poner precios redondos o con decimales?',
    a: 'Los estudios de psicologia de precios demuestran que los precios terminados en ,90 o ,95 generan mas ventas que los redondos en restaurantes casual. Sin embargo, en fine dining los precios redondos (sin decimales ni simbolo €) transmiten exclusividad. Adapta la estrategia a tu posicionamiento.',
  },
  {
    q: '¿Cada cuanto debo revisar los precios de mi carta?',
    a: 'Lo ideal es revisar precios cada vez que cambian los costes de materia prima, como minimo trimestralmente. Con la inflacion actual, muchos restaurantes revisan precios mensualmente. Un software como PLEKO te avisa automaticamente cuando el food cost de un plato supera tu objetivo.',
  },
  {
    q: '¿Como puedo subir precios sin perder clientes?',
    a: 'Las mejores estrategias son: 1) Subir gradualmente (5-8% maximo por revision). 2) Mejorar simultaneamente la presentacion o gramaje. 3) Crear nuevos platos con precio mas alto en vez de subir los existentes. 4) Usar ingenieria de menu para destacar platos con mejor margen. 5) Comunicar la calidad de los ingredientes.',
  },
];

/* ─── Pricing reference table data ───────────────────── */

const pricingTable = [
  { coste: 2, fc25: '8,00', fc30: '6,67', fc35: '5,71' },
  { coste: 3, fc25: '12,00', fc30: '10,00', fc35: '8,57' },
  { coste: 4, fc25: '16,00', fc30: '13,33', fc35: '11,43' },
  { coste: 5, fc25: '20,00', fc30: '16,67', fc35: '14,29' },
  { coste: 6, fc25: '24,00', fc30: '20,00', fc35: '17,14' },
  { coste: 7, fc25: '28,00', fc30: '23,33', fc35: '20,00' },
  { coste: 8, fc25: '32,00', fc30: '26,67', fc35: '22,86' },
  { coste: 10, fc25: '40,00', fc30: '33,33', fc35: '28,57' },
  { coste: 12, fc25: '48,00', fc30: '40,00', fc35: '34,29' },
  { coste: 15, fc25: '60,00', fc30: '50,00', fc35: '42,86' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como Calcular el Precio de Venta de un Plato en tu Restaurante',
  description:
    'Guia completa para fijar precios rentables en tu restaurante. Incluye formula del food cost, metodo del multiplicador, ejemplos practicos y psicologia de precios.',
  totalTime: 'PT20M',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Calcula el coste de materia prima del plato (escandallo)',
      text: 'Lista todos los ingredientes con sus cantidades, aplica mermas y multiplica por el precio de compra. Suma todos los costes para obtener el coste total por racion.',
      url: 'https://pleko.app/calcular-precio-plato#formula-basica',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Define tu food cost objetivo',
      text: 'Establece el porcentaje de food cost objetivo para tu tipo de establecimiento. El estandar es 28-32% para restaurantes casuales y 30-38% para fine dining.',
      url: 'https://pleko.app/calcular-precio-plato#food-cost-tipo',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Aplica la formula: PVP = Coste / Food Cost objetivo',
      text: 'Divide el coste del plato entre el food cost objetivo expresado en decimal. Si el coste es 6,29 € y quieres un food cost del 30%, el PVP minimo es 6,29 / 0,30 = 20,97 €.',
      url: 'https://pleko.app/calcular-precio-plato#formula-basica',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Valida con el metodo del multiplicador',
      text: 'Multiplica el coste de materia prima por 3 a 4 segun tu tipo de restaurante. Este metodo rapido debe dar un resultado similar a la formula del food cost.',
      url: 'https://pleko.app/calcular-precio-plato#multiplicador',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Ajusta segun factores externos',
      text: 'Considera costes fijos, personal, energia, competencia, posicionamiento y psicologia de precios antes de fijar el precio final en la carta.',
      url: 'https://pleko.app/calcular-precio-plato#factores-externos',
    },
  ],
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    {
      '@type': 'ListItem',
      position: 3,
      name: 'Calcular Precio Plato',
      item: 'https://pleko.app/calcular-precio-plato',
    },
  ],
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Como Calcular el Precio de Venta de un Plato en tu Restaurante',
  description:
    'Guia completa para fijar precios en tu restaurante: formula del food cost, multiplicador, ejemplos practicos de paella, postre y menu del dia, psicologia de precios y errores comunes.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: {
    '@type': 'Organization',
    name: 'PLEKO',
    logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' },
  },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-importa', label: 'Por que fijar bien el precio es vital' },
  { id: 'formula-basica', label: 'La formula basica: PVP = Coste / Food Cost' },
  { id: 'multiplicador', label: 'El metodo del multiplicador' },
  { id: 'ejemplo-paella', label: 'Ejemplo: fijar precio de una paella' },
  { id: 'ejemplo-postre', label: 'Ejemplo: fijar precio de un postre' },
  { id: 'ejemplo-menu', label: 'Ejemplo: fijar precio de un menu del dia' },
  { id: 'factores-externos', label: 'Factores mas alla del food cost' },
  { id: 'metodo-4-costes', label: 'El metodo de los 4 costes' },
  { id: 'psicologia-precios', label: 'Psicologia de precios en la carta' },
  { id: 'food-cost-tipo', label: 'Tabla: food cost por tipo de establecimiento' },
  { id: 'errores-comunes', label: 'Errores comunes al fijar precios' },
  { id: 'pleko-ayuda', label: 'Como PLEKO te ayuda a fijar precios' },
  { id: 'tabla-referencia', label: 'Tabla de referencia rapida de precios' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function CalcularPrecioPlatoPage() {
  return (
    <>
      <Helmet>
        <title>Como Calcular el Precio de Venta de un Plato en tu Restaurante | PLEKO</title>
        <meta
          name="description"
          content="Aprende a calcular el precio de venta de tus platos con la formula del food cost, el metodo del multiplicador y ejemplos practicos. Guia completa de pricing para restaurantes 2026."
        />
        <meta
          name="keywords"
          content="calcular precio plato, como fijar precio plato restaurante, pricing restaurante, precio de venta plato, formula precio venta restaurante, multiplicador precio restaurante, calcular pvp plato, precio carta restaurante, food cost precio venta"
        />
        <link rel="canonical" href="https://pleko.app/calcular-precio-plato" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/calcular-precio-plato" />
        <meta
          property="og:title"
          content="Como Calcular el Precio de Venta de un Plato en tu Restaurante"
        />
        <meta
          property="og:description"
          content="Guia completa de pricing para restaurantes: formula del food cost, multiplicador, ejemplos practicos y psicologia de precios."
        />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Como Calcular el Precio de Venta de un Plato | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Formula del food cost, multiplicador y ejemplos practicos para fijar precios rentables en tu restaurante."
        />
        <script type="application/ld+json">{JSON.stringify(howToJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <DollarSign className="w-4 h-4" />
                Guia de pricing para restaurantes — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Como Calcular el <span className="text-blue-500">Precio de Venta</span> de un Plato en tu Restaurante
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Aprende a fijar precios rentables con la <strong>formula del food cost</strong>, el <strong>metodo del multiplicador</strong> y ejemplos practicos de paella, postre y menu del dia. Incluye psicologia de precios y tabla de referencia por tipo de establecimiento.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#formula-basica"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors shadow-lg shadow-blue-200"
                >
                  Ver la formula
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calcular escandallo gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: POR QUE IMPORTA ═══════════ */}
            <h2 id="por-que-importa" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Por que fijar bien el precio de tus platos es vital
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El precio de venta de un plato no es solo un numero en la carta. Es la decision que determina si tu restaurante genera beneficios o pierde dinero cada dia. Un precio demasiado bajo destruye tu margen sin que te des cuenta; un precio demasiado alto ahuyenta a los clientes. El equilibrio esta en <strong>calcular el precio con datos, no con intuicion</strong>.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Segun datos del sector hostelero espanol, <strong>el 60% de los restaurantes que cierran en los primeros 3 anos no llevaban un control de costes adecuado</strong>. La razon principal: fijaban precios "a ojo", sin conocer el coste real de cada plato. Cuando los precios de la materia prima subian, sus margenes se evaporaban silenciosamente.
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="border border-slate-200 rounded-xl p-5 text-center">
                <TrendingUp className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-800 mb-2">Margen garantizado</h3>
                <p className="text-sm text-slate-600">
                  Saber exactamente cuanto ganas con cada plato que sirves. Sin sorpresas al final del mes.
                </p>
              </div>
              <div className="border border-slate-200 rounded-xl p-5 text-center">
                <Target className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-800 mb-2">Competitividad</h3>
                <p className="text-sm text-slate-600">
                  Precios alineados con tu mercado. Ni regalas comida ni pierdes clientes por ser caro.
                </p>
              </div>
              <div className="border border-slate-200 rounded-xl p-5 text-center">
                <Brain className="w-8 h-8 text-purple-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-800 mb-2">Percepcion de valor</h3>
                <p className="text-sm text-slate-600">
                  El precio comunica calidad. Un plato infravalorado genera desconfianza, no mas ventas.
                </p>
              </div>
            </div>

            {/* ═══════════ SECTION 2: FORMULA BASICA ═══════════ */}
            <h2 id="formula-basica" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              La formula basica: PVP = Coste / Food Cost objetivo
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La formula mas utilizada en hosteleria para calcular el precio de venta de un plato parte del <strong>coste de materia prima</strong> (lo que cuesta el plato en ingredientes) y del <strong>food cost objetivo</strong> (el porcentaje que quieres que represente la materia prima sobre el precio de venta).
            </p>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-6">
              <p className="text-blue-800 font-semibold text-center mb-3 text-lg">Formula del Precio de Venta</p>
              <p className="text-center text-3xl font-bold text-blue-600 font-mono">
                PVP = Coste del plato / Food Cost objetivo
              </p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-center">
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-slate-500 uppercase">Coste del plato</p>
                  <p className="font-bold text-slate-800">Escandallo (€)</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-slate-500 uppercase">Food Cost objetivo</p>
                  <p className="font-bold text-slate-800">0,25 – 0,35</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-100">
                  <p className="text-xs text-slate-500 uppercase">Resultado</p>
                  <p className="font-bold text-blue-600">PVP minimo</p>
                </div>
              </div>
              <p className="text-center text-slate-600 mt-4 text-sm">
                Ejemplo: Si tu plato cuesta <strong>6,00 €</strong> en ingredientes y quieres un food cost del <strong>30%</strong>: PVP = 6,00 / 0,30 = <strong>20,00 €</strong>
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Esta formula te da el <strong>precio minimo</strong> para alcanzar tu objetivo de food cost. A partir de ahi, puedes ajustar hacia arriba segun el valor percibido, la competencia y el posicionamiento de tu restaurante. Nunca hacia abajo, o estaras sacrificando margen.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Requisito previo: necesitas el escandallo</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Para usar esta formula, primero necesitas conocer el coste exacto de tu plato. Eso se obtiene con un <Link to="/escandallo" className="text-amber-800 underline hover:text-amber-900">escandallo</Link>: el desglose de todos los ingredientes con sus cantidades, mermas y precios. Si no tienes los escandallos hechos, empieza por ahi. Puedes usar nuestra <Link to="/calculadora-escandallo" className="text-amber-800 underline hover:text-amber-900">calculadora gratuita</Link>.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 3: MULTIPLICADOR ═══════════ */}
            <h2 id="multiplicador" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              El metodo del multiplicador: la forma rapida
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>metodo del multiplicador</strong> es una version simplificada de la formula anterior. En vez de dividir por el food cost, multiplicas el coste de materia prima por un factor. El resultado es equivalente:
            </p>

            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-6">
              <p className="text-green-800 font-semibold text-center mb-3 text-lg">Metodo del Multiplicador</p>
              <p className="text-center text-3xl font-bold text-green-600 font-mono">
                PVP = Coste &times; Multiplicador
              </p>
              <p className="text-center text-slate-600 mt-3 text-sm">
                El multiplicador es el inverso del food cost: si FC = 30%, multiplicador = 1/0,30 = 3,33
              </p>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de restaurante</th>
                    <th className="p-3 text-center font-medium">Multiplicador</th>
                    <th className="p-3 text-center font-medium">Food cost equiv.</th>
                    <th className="p-3 text-left font-medium">Ejemplo (coste 5 €)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Fast food / take-away', '&times;3,3 – &times;4,0', '25–30%', '16,50 – 20,00 €'],
                    ['Casual dining', '&times;2,9 – &times;3,5', '28–35%', '14,50 – 17,50 €'],
                    ['Restaurante medio-alto', '&times;3,0 – &times;3,5', '28–33%', '15,00 – 17,50 €'],
                    ['Fine dining', '&times;2,6 – &times;3,3', '30–38%', '13,00 – 16,50 €'],
                    ['Cafeteria / brunch', '&times;3,5 – &times;5,0', '20–28%', '17,50 – 25,00 €'],
                    ['Bar de copas', '&times;4,5 – &times;7,0', '15–22%', '22,50 – 35,00 €'],
                  ].map(([tipo, mult, fc, ejemplo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-green-600" dangerouslySetInnerHTML={{ __html: mult }} />
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{fc}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{ejemplo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              El multiplicador es util para hacer calculos rapidos de cabeza ("este plato me cuesta 7 €, asi que como minimo deberia venderlo a 21 €"). Pero para decisiones finales, siempre valida con la formula del food cost y considera los factores que veremos mas adelante.
            </p>

            {/* ═══════════ SECTION 4: EJEMPLO PAELLA ═══════════ */}
            <h2 id="ejemplo-paella" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Ejemplo practico: fijar el precio de una Paella Valenciana
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Vamos a calcular paso a paso el precio de venta de una Paella Valenciana para 1 racion, partiendo del escandallo completo:
            </p>

            <div className="border-2 border-blue-200 rounded-2xl overflow-hidden mb-6">
              <div className="bg-blue-500 text-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">Paella Valenciana</h3>
                    <p className="text-blue-100 text-sm mt-1">Escandallo por racion</p>
                  </div>
                  <ChefHat className="w-8 h-8 text-blue-200" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="p-3 text-left font-semibold text-slate-700">Ingrediente</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Cantidad</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Precio/kg</th>
                      <th className="p-3 text-right font-semibold text-slate-700">Coste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Arroz bomba', '80 g', '2,80 €/kg', '0,22 €'],
                      ['Pollo troceado', '120 g', '5,90 €/kg', '0,81 €'],
                      ['Judia verde ferradura', '30 g', '3,20 €/kg', '0,11 €'],
                      ['Garrofon', '20 g', '12,50 €/kg', '0,25 €'],
                      ['Tomate rallado', '40 g', '2,10 €/kg', '0,09 €'],
                      ['Aceite de oliva', '15 ml', '8,50 €/L', '0,13 €'],
                      ['Azafran', '0,15 g', '4.500 €/kg', '0,68 €'],
                      ['Caldo de pollo', '200 ml', '1,80 €/L', '0,38 €'],
                      ['Sal y especias', '—', '—', '0,02 €'],
                    ].map(([ing, cant, precio, coste], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cant}</td>
                        <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                        <td className="p-3 border-b border-slate-100 text-right font-semibold text-slate-800">{coste}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50">
                      <td colSpan={3} className="p-3 font-bold text-slate-800 text-base">COSTE POR RACION</td>
                      <td className="p-3 text-right font-bold text-blue-600 text-lg">2,69 €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Ahora apliquemos las dos formulas para fijar el PVP:
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                <p className="text-blue-800 font-semibold mb-2">Formula del Food Cost (objetivo 30%)</p>
                <p className="text-center text-xl font-bold text-blue-600 font-mono">
                  PVP = 2,69 € / 0,30 = <span className="text-2xl">8,97 €</span>
                </p>
              </div>
              <div className="bg-green-50 border border-green-100 rounded-xl p-6">
                <p className="text-green-800 font-semibold mb-2">Multiplicador (&times;3,33)</p>
                <p className="text-center text-xl font-bold text-green-600 font-mono">
                  PVP = 2,69 € &times; 3,33 = <span className="text-2xl">8,96 €</span>
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El precio minimo para mantener un food cost del 30% es <strong>~9 €</strong>. Considerando que una paella es un plato principal con alto valor percibido, un restaurante casual en Espana puede fijar el PVP entre <strong>12 € y 16 €</strong> por racion (food cost real del 17-22%), lo que deja un margen excelente. Si trabajas con paella para compartir (2-3 personas), el PVP por persona baja pero el ticket medio sube.
            </p>

            <div className="grid md:grid-cols-3 gap-3 mb-10">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP conservador</p>
                <p className="text-2xl font-bold text-green-600">12,00 €</p>
                <p className="text-xs text-slate-400">FC = 22,4%</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP medio</p>
                <p className="text-2xl font-bold text-blue-600">14,50 €</p>
                <p className="text-xs text-slate-400">FC = 18,6%</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP premium</p>
                <p className="text-2xl font-bold text-purple-600">18,00 €</p>
                <p className="text-xs text-slate-400">FC = 14,9%</p>
              </div>
            </div>

            {/* ═══════════ SECTION 5: EJEMPLO POSTRE ═══════════ */}
            <h2 id="ejemplo-postre" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Ejemplo: fijar el precio de un postre (Crema Catalana)
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los postres suelen tener un food cost mas bajo que los platos principales, lo que los convierte en generadores de margen. Veamos el ejemplo de una Crema Catalana:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-right font-medium">Coste</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Leche entera', '200 ml', '0,22 €'],
                    ['Yemas de huevo (3)', '60 g', '0,45 €'],
                    ['Azucar', '30 g', '0,03 €'],
                    ['Maizena', '10 g', '0,02 €'],
                    ['Piel de limon', '5 g', '0,01 €'],
                    ['Canela en rama', '1 g', '0,03 €'],
                    ['Azucar para caramelizar', '15 g', '0,02 €'],
                  ].map(([ing, cant, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{cant}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={2} className="p-3 font-bold text-slate-800">COSTE TOTAL</td>
                    <td className="p-3 text-right font-bold text-amber-600 text-lg">0,78 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                <p className="text-blue-800 font-semibold mb-2">PVP con food cost 25%</p>
                <p className="text-center text-xl font-bold text-blue-600 font-mono">
                  0,78 € / 0,25 = <span className="text-2xl">3,12 €</span>
                </p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Con un coste de solo 0,78 €, una Crema Catalana vendida a <strong>5,50 - 7,00 €</strong> genera un food cost del 11-14% y un margen bruto de 4,72 - 6,22 € por unidad. Los postres y las bebidas son los grandes aliados de la rentabilidad. Por eso la <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">ingenieria de menu</Link> recomienda destacarlos en la carta.
            </p>

            {/* ═══════════ SECTION 6: EJEMPLO MENU DEL DIA ═══════════ */}
            <h2 id="ejemplo-menu" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Ejemplo: fijar el precio de un menu del dia
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El menu del dia es un caso especial: se acepta un food cost mas alto (30-35%) porque genera volumen y fideliza clientes. La clave esta en equilibrar platos con diferente coste:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Componente</th>
                    <th className="p-3 text-left font-medium">Opciones</th>
                    <th className="p-3 text-right font-medium">Coste medio</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Primer plato', 'Ensalada / Sopa / Pasta', '1,80 €'],
                    ['Segundo plato', 'Pollo / Cerdo / Pescado del dia', '3,20 €'],
                    ['Postre', 'Fruta / Flan / Helado', '0,60 €'],
                    ['Bebida', 'Agua / Refresco / Copa de vino', '0,40 €'],
                    ['Pan', 'Barra del dia', '0,15 €'],
                  ].map(([comp, opciones, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{comp}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{opciones}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50">
                    <td colSpan={2} className="p-3 font-bold text-slate-800 text-base">COSTE TOTAL MENU</td>
                    <td className="p-3 text-right font-bold text-blue-600 text-lg">6,15 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-3 gap-3 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP (FC 30%)</p>
                <p className="text-2xl font-bold text-green-600">20,50 €</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP (FC 35%)</p>
                <p className="text-2xl font-bold text-blue-600">17,57 €</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1 uppercase">PVP tipico Espana</p>
                <p className="text-2xl font-bold text-amber-600">14 – 18 €</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Un menu del dia a 15 € con un coste de 6,15 € tiene un food cost del 41%, que parece alto. Pero si sirves 40 menus al dia, generas un margen bruto de <strong>354 € diarios</strong> (40 &times; 8,85 €). La clave del menu del dia es el <strong>volumen</strong>: compensa margenes mas ajustados con rotacion alta. Ademas, muchos clientes del menu vuelven por la noche con gasto medio mayor.
            </p>

            {/* ═══════════ SECTION 7: FACTORES EXTERNOS ═══════════ */}
            <h2 id="factores-externos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Factores mas alla del food cost
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La formula del food cost solo considera el coste de materia prima. Pero un restaurante tiene muchos mas costes que cubrir antes de generar beneficio. Estos son los principales factores que debes considerar al fijar tus precios:
            </p>

            <div className="space-y-4 mb-10">
              {[
                { title: 'Coste de personal', desc: 'Salarios, seguridad social, uniformes. Representa entre el 25% y el 35% de la facturacion en la mayoria de restaurantes. Un plato que requiere 20 minutos de preparacion por un chef a 15 €/hora anade 5 € de coste que no aparece en el escandallo.', pct: '25–35%' },
                { title: 'Alquiler y gastos fijos', desc: 'Local, seguros, contabilidad, licencias, amortizacion del equipamiento. Son costes que pagas vendas 10 platos o 200. Cuanto mayor sea tu volumen, menor sera el impacto por plato.', pct: '10–20%' },
                { title: 'Energia y suministros', desc: 'Electricidad, gas, agua, productos de limpieza, menaje, embalajes. La cocina de un restaurante medio consume entre 3.000 € y 8.000 € al mes en suministros.', pct: '5–8%' },
                { title: 'Amortizaciones', desc: 'Horno, camaras frigorificas, mobiliario, decoracion, reformas. Un horno combinado de 15.000 € amortizado en 10 anos son 125 €/mes que debe cubrir la facturacion.', pct: '3–5%' },
                { title: 'Marketing y tecnologia', desc: 'Redes sociales, plataformas de reservas, software de gestion, TPV, web. Cada vez pesan mas en la estructura de costes.', pct: '2–5%' },
              ].map(({ title, desc, pct }) => (
                <div key={title} className="border-l-4 border-blue-400 pl-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">
                    {title} <span className="text-blue-500 font-normal text-base">({pct} de facturacion)</span>
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 8: METODO 4 COSTES ═══════════ */}
            <h2 id="metodo-4-costes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              El metodo de los 4 costes
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Este metodo, mas preciso que el simple food cost, calcula el precio de venta sumando los cuatro bloques principales de coste de un restaurante y anadiendo un margen de beneficio:
            </p>

            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-8 mb-6">
              <p className="text-purple-800 font-semibold text-center mb-3 text-lg">Formula de los 4 Costes</p>
              <p className="text-center text-2xl font-bold text-purple-600 font-mono">
                PVP = Materia prima + Personal + Gastos fijos + Beneficio
              </p>
              <div className="mt-6 grid sm:grid-cols-4 gap-3 text-center">
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <p className="text-xs text-slate-500 uppercase">Materia prima</p>
                  <p className="font-bold text-orange-600">28–32%</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <p className="text-xs text-slate-500 uppercase">Personal</p>
                  <p className="font-bold text-blue-600">28–35%</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <p className="text-xs text-slate-500 uppercase">Gastos fijos</p>
                  <p className="font-bold text-slate-600">18–25%</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <p className="text-xs text-slate-500 uppercase">Beneficio</p>
                  <p className="font-bold text-green-600">8–15%</p>
                </div>
              </div>
              <p className="text-center text-slate-600 mt-4 text-sm">
                Ejemplo: Plato con coste MP = 5 €. Si MP = 30% del PVP &rarr; PVP = 5 / 0,30 = 16,67 €.
                <br />De esos 16,67 €: ~5,00 € MP + ~5,00 € personal + ~3,75 € fijos + ~1,92 € beneficio.
              </p>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Si la suma de costes supera el PVP que tu mercado acepta, tienes un problema estructural: necesitas reducir costes (negociar alquiler, optimizar plantilla) o reposicionar tu oferta. El metodo de los 4 costes te permite detectar este problema antes de que sea demasiado tarde.
            </p>

            {/* ═══════════ SECTION 9: PSICOLOGIA DE PRECIOS ═══════════ */}
            <h2 id="psicologia-precios" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Psicologia de precios en la carta
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Una vez calculado el precio con las formulas anteriores, la forma en que lo presentas en la carta puede aumentar o reducir las ventas significativamente. Estas son las tecnicas de psicologia de precios mas efectivas en restauracion:
            </p>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Precios sin simbolo de moneda</h3>
                <p className="text-slate-700 leading-relaxed">
                  Estudios de la Universidad de Cornell demuestran que los clientes gastan mas cuando el precio aparece como "14" o "14,00" en vez de "14 €" o "14,00 €". El simbolo del euro activa la "sensacion de gasto" en el cerebro. En fine dining, eliminar el simbolo es practica habitual.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Precios terminados en ,90 vs redondos</h3>
                <p className="text-slate-700 leading-relaxed">
                  En restaurantes casuales, los precios terminados en ,90 o ,95 (14,90 € en vez de 15 €) generan la percepcion de "mas barato" y aumentan la conversion. En establecimientos premium, los precios redondos sin decimales transmiten exclusividad y confianza. Adapta la estrategia a tu posicionamiento.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">El efecto anclaje</h3>
                <p className="text-slate-700 leading-relaxed">
                  Coloca un plato premium muy caro al inicio de cada seccion (por ejemplo, un chuleton de buey a 45 €). Esto hace que el resto de platos parezcan "razonables" por comparacion. El anclaje no se vende mucho, pero eleva el ticket medio de toda la seccion.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">El efecto "decoy" (senuelo)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Ofrece tres opciones: una economica, una cara y una intermedia con la mejor relacion calidad-precio. La mayoria de clientes elegiran la intermedia, que es exactamente la que tiene mejor margen para ti. Ejemplo: Ensalada 9 €, Risotto 14 € (tu objetivo), Solomillo 24 €.
                </p>
              </div>

              <div className="border-l-4 border-purple-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">No alinear precios a la derecha</h3>
                <p className="text-slate-700 leading-relaxed">
                  Cuando los precios estan alineados en columna, el cliente los compara automaticamente y elige el mas barato. En vez de eso, coloca el precio discretamente despues de la descripcion del plato, separado por puntos suspensivos. El foco pasa del precio a la experiencia.
                </p>
              </div>
            </div>

            {/* ═══════════ SECTION 10: FOOD COST POR TIPO ═══════════ */}
            <h2 id="food-cost-tipo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla de referencia: food cost por tipo de establecimiento
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              No todos los restaurantes deben tener el mismo food cost. Cada tipo de establecimiento opera con estructuras de costes diferentes. Esta tabla te sirve como referencia para definir tu food cost objetivo:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de establecimiento</th>
                    <th className="p-3 text-center font-medium">Food cost objetivo</th>
                    <th className="p-3 text-center font-medium">Multiplicador</th>
                    <th className="p-3 text-left font-medium">Caracteristicas</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Fast food / comida rapida', '25–30%', '&times;3,3 – &times;4,0', 'Alto volumen, baja complejidad, personal minimo'],
                    ['Casual dining', '28–35%', '&times;2,9 – &times;3,5', 'Equilibrio volumen/margen, carta variada'],
                    ['Fine dining', '30–38%', '&times;2,6 – &times;3,3', 'Bajo volumen, alta calidad, personal cualificado'],
                    ['Cafeteria / brunch', '20–28%', '&times;3,5 – &times;5,0', 'Bebidas de alto margen compensan comida'],
                    ['Bar de copas', '15–22%', '&times;4,5 – &times;7,0', 'Bebidas con margenes muy altos'],
                    ['Buffet / all-you-can-eat', '35–45%', '&times;2,2 – &times;2,9', 'Compensa con volumen extremo'],
                    ['Dark kitchen / delivery', '28–32%', '&times;3,1 – &times;3,6', 'Sin costes de sala, pero comisiones plataformas'],
                    ['Catering', '30–40%', '&times;2,5 – &times;3,3', 'Eventos: presupuestos cerrados, alto volumen'],
                  ].map(([tipo, fc, mult, caract], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-blue-600">{fc}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600" dangerouslySetInnerHTML={{ __html: mult }} />
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-xs">{caract}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-blue-800">Dato clave</p>
                  <p className="text-blue-700 text-sm mt-1">
                    El food cost medio en Espana para restaurantes de servicio completo se situa entre el <strong>28% y el 32%</strong>. Si tu food cost global esta por encima del 35%, es urgente revisar precios, proveedores o recetas. Con PLEKO puedes ver el food cost de cada plato en tiempo real y recibir alertas automaticas.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 11: ERRORES COMUNES ═══════════ */}
            <h2 id="errores-comunes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Errores comunes al fijar precios en un restaurante
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. Copiar los precios de la competencia</h3>
                <p className="text-slate-700 leading-relaxed">
                  Cada restaurante tiene costes diferentes: distinto alquiler, distinta plantilla, distintos proveedores. Que tu vecino venda la paella a 14 € no significa que ese precio sea rentable para ti. Calcula tus propios costes y fija tus propios precios. La competencia es una referencia, no un modelo.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. No actualizar precios cuando suben los costes</h3>
                <p className="text-slate-700 leading-relaxed">
                  Muchos hosteleros mantienen precios de carta durante anos mientras los costes de materia prima, energia y personal suben constantemente. Resultado: margenes que se evaporan lentamente. Revisa precios al menos trimestralmente y no tengas miedo de ajustar. Los clientes entienden las subidas si la calidad se mantiene.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. Poner el mismo margen a todos los platos</h3>
                <p className="text-slate-700 leading-relaxed">
                  Aplicar un multiplicador uniforme (por ejemplo, &times;3 a todo) ignora que cada plato tiene un valor percibido diferente. Un postre puede soportar un multiplicador &times;5 porque el cliente lo ve como un "capricho", mientras que un plato principal con ingredientes comunes dificilmente aguanta mas de &times;3. La <Link to="/ingenieria-de-menu" className="text-blue-600 hover:underline">ingenieria de menu</Link> te ayuda a optimizar cada plato individualmente.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Infravalorar platos para atraer clientes</h3>
                <p className="text-slate-700 leading-relaxed">
                  Vender barato no es una estrategia de negocio, es una forma de cerrar lentamente. Un precio demasiado bajo genera desconfianza ("si es tan barato, que calidad tendra"), atrae al cliente que busca precio (y se ira al primer descuento de otro restaurante) y destruye tu margen. Compite por calidad y experiencia, no por precio.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. Fijar precios sin conocer el escandallo</h3>
                <p className="text-slate-700 leading-relaxed">
                  Es el error mas grave de todos. Si no sabes cuanto te cuesta cada plato, estas fijando precios a ciegas. Un plato que "parece barato de hacer" puede tener un food cost del 50% por culpa de mermas elevadas, ingredientes olvidados o precios desactualizados. El escandallo es el primer paso obligatorio. <Link to="/como-hacer-escandallo" className="text-blue-600 hover:underline">Aprende a hacerlo paso a paso</Link>.
                </p>
              </div>
            </div>

            {/* ═══════════ SECTION 12: PLEKO ═══════════ */}
            <h2 id="pleko-ayuda" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como PLEKO te ayuda a fijar precios automaticamente
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Calcular el precio de un plato manualmente es educativo, pero gestionar los precios de toda una carta con 30, 50 o 100 recetas es otra cosa. PLEKO automatiza todo el proceso:
            </p>

            <div className="space-y-3 mb-6">
              {[
                'Escandallo automatico de cada receta con precios siempre actualizados',
                'Escanea tus albaranes con el movil (OCR) y todos los costes se recalculan al instante',
                'Semaforo de food cost en tiempo real: verde, amarillo, rojo para cada plato',
                'PVP sugerido automatico basado en tu food cost objetivo configurado',
                'Alertas cuando un plato supera el food cost maximo que has definido',
                'Comparacion de precios con mercados mayoristas (Mercabarna, Mercamadrid)',
                'Ingenieria de menu: clasifica platos en Stars, Plowhorses, Puzzles y Dogs',
                'Simulador de precios: prueba diferentes PVPs y ve el impacto en el margen',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              Con PLEKO, no necesitas calcular nada manualmente. Solo introduces tus ingredientes una vez, escaneas las facturas cuando llegan y el sistema te dice en todo momento si tus precios son rentables. Si el precio de la lubina sube un 10%, te avisa y te sugiere el nuevo PVP.
            </p>

            {/* ═══════════ SECTION 13: TABLA DE REFERENCIA RAPIDA ═══════════ */}
            <h2 id="tabla-referencia" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla de referencia rapida: de coste a precio de venta
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Usa esta tabla para convertir rapidamente el coste de materia prima en precio de venta segun el food cost objetivo:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-right font-medium">Coste MP</th>
                    <th className="p-3 text-center font-medium">
                      <span className="block">PVP</span>
                      <span className="text-xs font-normal text-slate-300">(FC 25%)</span>
                    </th>
                    <th className="p-3 text-center font-medium">
                      <span className="block">PVP</span>
                      <span className="text-xs font-normal text-slate-300">(FC 30%)</span>
                    </th>
                    <th className="p-3 text-center font-medium">
                      <span className="block">PVP</span>
                      <span className="text-xs font-normal text-slate-300">(FC 35%)</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pricingTable.map(({ coste, fc25, fc30, fc35 }, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 text-right font-bold text-slate-800">{coste},00 €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-green-600 font-semibold">{fc25} €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-blue-600 font-semibold">{fc30} €</td>
                      <td className="p-3 border-b border-slate-100 text-center text-amber-600 font-semibold">{fc35} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              <strong>Como leer la tabla:</strong> si tu plato cuesta 5 € en ingredientes y quieres un food cost del 30%, el PVP minimo es <strong>16,67 €</strong>. Si puedes venderlo a 20 €, tu food cost real sera del 25% — un margen excelente.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Recuerda: estos son precios minimos</p>
                  <p className="text-red-700 text-sm mt-1">
                    Los PVP de esta tabla solo cubren el food cost objetivo. El precio final debe incluir un margen que cubra tambien personal, alquiler, energia y beneficio. Usa la tabla como referencia rapida, pero valida siempre con el metodo de los 4 costes para decisiones definitivas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre pricing en restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online (gratis)' },
                { to: '/food-cost', label: 'Food Cost: que es y como calcularlo' },
                { to: '/calculadora-food-cost', label: 'Calculadora de Food Cost gratuita' },
                { to: '/margen-beneficio-restaurante', label: 'Margen de beneficio en restaurantes' },
                { to: '/ingenieria-de-menu', label: 'Ingenieria de menu: Stars, Plowhorses, Puzzles' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/software-escandallo', label: 'El mejor software de escandallos' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Calcula el precio optimo de toda tu carta
            </h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula automaticamente el coste y PVP sugerido de todas tus recetas, controla food cost en tiempo real y te avisa cuando un plato deja de ser rentable. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-blue-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
